import React, { FC } from 'react';
import styled from 'styled-components';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import { useAppSelector } from '../../redux/hooks';
import { ScansService } from '../../services/scans.service';
import OvScanList, { ScanListFieldOptions } from '../UI/organisms/OvScanList';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';
import { ClinicLocationService } from '../../services/clinic-location.service';
import { getAxiosErrorMessage } from '../../common/utils/helpers';

const ClinicLocationScanList: FC = () => {
  const selectedClinicLocation: ClinicLocation | undefined = useAppSelector(
    (state) => state.user.selectedClinicLocation
  );
  const isUserDataLoading: boolean = useAppSelector(
    (state) => state.user.isLoading
  );
  const [offset, setOffset] = useQueryParam(
    'offset',
    withDefault(NumberParam, 0),
    { updateType: 'replaceIn' }
  );

  const query = queryString.stringify({
    limit: ScansService.ADMIN_SCAN_LIST_LIMIT,
    offset,
  });
  const {
    data: paginatedScans,
    isFetching: isScanListFetching,
    error,
  } = useQuery({
    queryKey: ['clinic-location-scans', selectedClinicLocation?.id, query],
    queryFn: () =>
      ClinicLocationService.getScansForClinicLocation(
        selectedClinicLocation?.id,
        query
      ),
    enabled: !!selectedClinicLocation?.id,
  });
  const scans = paginatedScans?.docs ?? [];
  const isLoading = isScanListFetching || isUserDataLoading;

  const listFieldOptions: ScanListFieldOptions = {
    showOperation: false,
    showDetailsAction: false,
    showUserName: true,
    useAdminPath: false,
  };

  const goToPage = (event: any, value: number) => {
    setOffset((value - 1) * ScansService.ADMIN_SCAN_LIST_LIMIT || undefined);
  };

  return (
    <Container>
      <CenterPane>
        <OvPaginationBar
          total={paginatedScans?.total ?? 0}
          limit={ScansService.ADMIN_SCAN_LIST_LIMIT}
          offset={offset}
          onChange={goToPage}
        />

        {error && (
          <ErrorMessageWrapper>
            <OvErrorMessage message={getAxiosErrorMessage(error)} />
          </ErrorMessageWrapper>
        )}

        <OvScanList
          scans={scans}
          isLoading={isLoading}
          fieldOptions={listFieldOptions}
        />
      </CenterPane>
    </Container>
  );
};

export default ClinicLocationScanList;

const Container = styled.section`
  display: flex;
`;

const CenterPane = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.SMALL};
  flex-grow: 1;
`;

const ErrorMessageWrapper = styled.div`
  margin-top: 1rem;
`;
